import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import './InBondTransferInvoice.css';

type Party = {
  siteId: string;
  name: string;
  address: string;
  ttbNumber: string;
  siteName?: string;
};

type SizeRow = { size: string; quantity: number; barrels: number };
type BulkRow = { containerType: string; identity: string; barrels: number; quantity: number };

type InvoicePayload = {
  title: string;
  regulationMark: string;
  regulationCite: string;
  serialNumber: string;
  transferNumber: string | null;
  toNumber: string | null;
  shipmentDate: string | null;
  shipper: Party;
  receiver: Party;
  kegs: SizeRow[];
  cases: SizeRow[];
  bulk: BulkRow[];
  totalBarrels: number;
  receiving: { dateReceived: string; discrepancies: string };
};

function fmtBbl(n: number) {
  return Number(n || 0).toFixed(2);
}

function fmtQty(n: number) {
  const x = Number(n) || 0;
  return Number.isInteger(x) ? String(x) : x.toFixed(2);
}

const InBondTransferInvoice: React.FC = () => {
  const { transferId } = useParams<{ transferId: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<InvoicePayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch(
          `${API_BASE_URL}/api/transfers/${transferId}/in-bond-invoice`,
          { headers: { Accept: 'application/json' } }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        setDoc(await res.json());
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load transfer invoice');
      }
    };
    load();
  }, [transferId]);

  const printDoc = () => {
    document.body.classList.add('inbond-printing');
    const cleanup = () => {
      document.body.classList.remove('inbond-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  if (error) {
    return (
      <div className="page-container inbond-screen">
        <div className="inbond-toolbar no-print">
          <button
            type="button"
            className="inbond-btn-secondary"
            onClick={() => navigate(`/transfers/${transferId}`)}
            data-testid="inbond-close"
          >
            Close
          </button>
        </div>
        <div className="inbond-error" data-testid="inbond-error">
          {error}
        </div>
      </div>
    );
  }

  if (!doc) {
    return <div className="page-container inbond-screen">Loading transfer invoice…</div>;
  }

  return (
    <div className="page-container inbond-screen">
      <div className="inbond-toolbar no-print">
        <button type="button" className="inbond-btn-primary" onClick={printDoc} data-testid="inbond-print">
          Print
        </button>
        <button
          type="button"
          className="inbond-btn-secondary"
          onClick={() => navigate(`/transfers/${transferId}`)}
          data-testid="inbond-close"
        >
          Close
        </button>
      </div>

      <article className="inbond-page" data-testid="inbond-page">
        <h1>{doc.title}</h1>
        <p className="inbond-mark">{doc.regulationMark}</p>
        <p className="inbond-cite">{doc.regulationCite}</p>

        <div className="inbond-meta">
          <div>
            <strong>Serial number:</strong> {doc.serialNumber}
          </div>
          <div>
            <strong>Date of shipment:</strong> {doc.shipmentDate || '—'}
          </div>
          {doc.transferNumber && doc.transferNumber !== doc.serialNumber && (
            <div>
              <strong>Transfer #:</strong> {doc.transferNumber}
            </div>
          )}
          {doc.toNumber && (
            <div>
              <strong>TO #:</strong> {doc.toNumber}
            </div>
          )}
        </div>

        <section className="inbond-parties">
          <div data-testid="inbond-shipper">
            <h2>Shipping brewery</h2>
            <p className="inbond-party-name">{doc.shipper.name}</p>
            <p>{doc.shipper.address}</p>
            <p>
              <strong>TTB brewery registry no.:</strong> {doc.shipper.ttbNumber}
            </p>
          </div>
          <div data-testid="inbond-receiver">
            <h2>Receiving brewery</h2>
            <p className="inbond-party-name">{doc.receiver.name}</p>
            <p>{doc.receiver.address}</p>
            <p>
              <strong>TTB brewery registry no.:</strong> {doc.receiver.ttbNumber}
            </p>
          </div>
        </section>

        {doc.kegs.length > 0 && (
          <section>
            <h2>Kegs</h2>
            <table className="inbond-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Size</th>
                  <th className="num">Barrels</th>
                </tr>
              </thead>
              <tbody>
                {doc.kegs.map((row) => (
                  <tr key={row.size}>
                    <td className="num">{fmtQty(row.quantity)}</td>
                    <td>{row.size}</td>
                    <td className="num">{fmtBbl(row.barrels)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {doc.cases.length > 0 && (
          <section>
            <h2>Cases</h2>
            <table className="inbond-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Size</th>
                  <th className="num">Barrels</th>
                </tr>
              </thead>
              <tbody>
                {doc.cases.map((row) => (
                  <tr key={row.size}>
                    <td className="num">{fmtQty(row.quantity)}</td>
                    <td>{row.size}</td>
                    <td className="num">{fmtBbl(row.barrels)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {doc.bulk.length > 0 && (
          <section>
            <h2>Bulk containers</h2>
            <table className="inbond-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Identity</th>
                  <th className="num">Barrels</th>
                </tr>
              </thead>
              <tbody>
                {doc.bulk.map((row, i) => (
                  <tr key={`${row.identity}-${i}`}>
                    <td>{row.containerType}</td>
                    <td>{row.identity}</td>
                    <td className="num">{fmtBbl(row.barrels)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <p className="inbond-total" data-testid="inbond-total-bbl">
          Total barrels: {fmtBbl(doc.totalBarrels)}
        </p>

        <section className="inbond-receiving" data-testid="inbond-receiving">
          <h2>Receiving brewery notation (27 CFR 25.186)</h2>
          <p>
            On receipt, note the date received and any discrepancies in the beer received. Retain
            this invoice in the brewery records.
          </p>
          <div>
            <strong>Date received:</strong>
            <div className="inbond-blank-line" />
          </div>
          <div>
            <strong>Discrepancies (if any):</strong>
            <div className="inbond-blank-box" />
          </div>
        </section>
      </article>
    </div>
  );
};

export default InBondTransferInvoice;
